import { useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';
import BubbleLoader from 'App/Components/Loaders/BubbleLoader';
import { ToastHost } from 'App/Components/WarningToaster';
import { getConnectionEffectiveType } from 'App/Helpers/getConnectionEffectiveType';
import { initStorageAccess } from 'App/Helpers/localStorageHandler';
import initReactSentry from 'App/Services/ErrorTracking/initReactSentry';
import { installAdErrorHandler } from 'App/Services/ErrorTracking/installAdErrorHandler';
import { setDeviceInfoTags } from 'App/Services/ErrorTracking/setDeviceInfoTags';

/**
 * Sentry initialization gate.
 *
 * Adds global tags (`location`, `environment`, `net_type`, and optionally `source`)
 * **once** and renders a fallback loader until the SDK is ready.
 *
 * For multi-locale game landings, omit `source` here and set it later via
 * `Sentry.setTag('source', ...)` once the URL locale is known (e.g. in AppProvider).
 *
 * @typedef  {Object} SentryWrapperProps
 * @property {string} [source] – optional logical source/app identifier attached to every event at init time
 * @property {string} environment – Sentry environment (e.g. `"production"`, `"staging"`)
 * @property {React.ReactNode} children – subtree to render after Sentry is ready
 * @returns {JSX.Element} - BubbleLoader while initializing, otherwise the provided children.
 */
const AppBootstrap = ({ source, environment, dsn, children }) => {
    const [appReady, setAppReady] = useState(false);

    useEffect(() => {
        initReactSentry({
            location: document.location.host,
            environment,
            source,
            dsn,
        });
        const uninstallAdErrorHandler = installAdErrorHandler();
        const localStorageAvailable = initStorageAccess();

        setAppReady(localStorageAvailable);

        Sentry.setTag('net_type', getConnectionEffectiveType());
        void setDeviceInfoTags();

        return () => uninstallAdErrorHandler();
    }, []);

    if (!appReady)
        return (
            <BubbleLoader loaderColor={'#09328d'} backgroundColor={'#b7d6fe'} />
        );

    return (
        <>
            {children}
            <ToastHost />
        </>
    );
};

export { AppBootstrap };
